import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiAlert } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import {
  resolveCompanyIdentifierRule,
  isCompanyIdentifierRequired,
} from '../../constants/company-identifiers'
import { $api } from '../../api'
import { useDebounce } from '../../hooks/useDebounce'

interface CompanyIdentifierFieldProps {
  org: string
  /** The current country property value (ISO 3166-1 alpha-2) */
  country: string
  /** The current legal form (drives the identifier; falls back to country) */
  legalForm?: string
  /** Current properties object */
  properties: Record<string, unknown>
  /** Callback when the identifier value changes */
  onPropertyChange: (code: string, value: string) => void
  /** Current form errors */
  errors: Record<string, string>
  /** Callback to clear a form error */
  clearError: (field: string) => void
}

export function CompanyIdentifierField({
  org,
  country,
  legalForm,
  properties,
  onPropertyChange,
  errors,
  clearError,
}: CompanyIdentifierFieldProps) {
  const { t } = useTranslation('users')
  const rule = resolveCompanyIdentifierRule(legalForm, country)
  const required = isCompanyIdentifierRequired(rule)

  const identifierValue = rule ? String(properties[rule.propertyCode] || '') : ''
  const debouncedValue = useDebounce(identifierValue, 400)

  // Duplicate detection query
  const { data: duplicateResponse } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/users',
    {
      params: {
        path: { orgCode: org },
        query: {
          type: 'company' as const,
          [`property.${rule?.propertyCode}`]: debouncedValue,
          limit: 5,
        } as any,
      },
    },
    {
      enabled: !!rule && !!debouncedValue && debouncedValue.length >= 3,
    }
  )

  const duplicates = useMemo(() => {
    if (!duplicateResponse?.data) return []
    return (duplicateResponse.data as any[])
  }, [duplicateResponse])

  if (!rule) return null

  const identifierLabel = t(rule.labelKey)
  const hasError = !!errors[`identifier_${rule.propertyCode}`]

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          {identifierLabel}{' '}
          {required ? (
            <span className="text-destructive">*</span>
          ) : (
            <span className="text-xs font-normal text-muted-foreground">
              ({t('company.identifier.optional')})
            </span>
          )}
        </label>
        <input
          type="text"
          value={identifierValue}
          onChange={(e) => {
            onPropertyChange(rule.propertyCode, e.target.value)
            if (errors[`identifier_${rule.propertyCode}`]) {
              clearError(`identifier_${rule.propertyCode}`)
            }
          }}
          placeholder={rule.placeholder}
          className={`w-full border rounded-md px-3 py-2 bg-background text-foreground ${
            hasError ? 'border-destructive' : 'border-border'
          }`}
          data-testid={`company-identifier-${rule.propertyCode}`}
        />
        {rule.formatHint && (
          <p className="text-xs text-muted-foreground mt-1">
            {rule.formatHint.replace(/\\/g, '')}
          </p>
        )}
        {hasError && (
          <p className="text-destructive text-sm mt-1">
            {errors[`identifier_${rule.propertyCode}`]}
          </p>
        )}
      </div>

      {/* Duplicate warning */}
      {duplicates.length > 0 && (
        <div
          className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3"
          data-testid="duplicate-company-warning"
        >
          <div className="flex items-start gap-2">
            <AppIcon path={mdiAlert} size="sm" className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                {t('company.identifier.duplicate_warning', { identifier: identifierLabel })}
              </p>
              <ul className="space-y-1">
                {duplicates.map((dup: any) => (
                  <li key={dup.uid} className="text-sm">
                    <Link
                      to="/$org/users/$userUid"
                      params={{ org, userUid: dup.uid }}
                      className="text-amber-700 dark:text-amber-300 hover:underline"
                      data-testid={`duplicate-link-${dup.uid}`}
                    >
                      {dup.name || dup.email} — {t('company.identifier.duplicate_link')}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Validate company identifier requirement.
 * Returns error message if validation fails, undefined otherwise.
 */
export function validateCompanyIdentifier(
  userType: string,
  properties: Record<string, unknown>,
  t: (key: string, opts?: any) => string,
): { field: string; message: string } | undefined {
  if (userType !== 'company') return undefined

  const country = String(properties.country || '').toLowerCase()
  const legalForm = String(properties.legalForm || 'company')
  const rule = resolveCompanyIdentifierRule(legalForm, country)
  // No rule, or an optional identifier (e.g. syndicat without RNC) → never block.
  if (!rule || !isCompanyIdentifierRequired(rule)) return undefined

  const value = String(properties[rule.propertyCode] || '').trim()
  if (!value) {
    return {
      field: `identifier_${rule.propertyCode}`,
      message: t('company.identifier.required', { country: country.toUpperCase() }),
    }
  }

  return undefined
}
